'use client';

import { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { Star, ArrowLeftRight } from 'lucide-react';
import SmartLogo from '@/components/tool/SmartLogo';
import { Badge } from '@/components/ui/badge';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { getSubCategorySlug } from '@/lib/data';
import { toFrenchProfessionSlug } from '@/lib/i18n';

interface FeaturedTool {
  id: string;
  name: string;
  slug: string;
  profession_slug: string;
  description_short: string;
  logo_url: string | null;
  website_url: string | null;
  pricing: any[] | null;
  categories: string[];
  view_count: number | null;
}

interface ToolBubbleSliderProps {
  recentTools: FeaturedTool[];
  localePrefix: string;
  locale: string;
  professionNameMap: Record<string, string>;
}

export default function ToolBubbleSlider({
  recentTools,
  localePrefix,
  locale,
  professionNameMap,
}: ToolBubbleSliderProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [cardsPerPage, setCardsPerPage] = useState(4);

  // Handle window resizing to calculate responsive pages
  useEffect(() => {
    function handleResize() {
      if (!scrollRef.current) return;
      const width = window.innerWidth;
      let itemsPerSlide = 4;
      if (width < 640) itemsPerSlide = 1;
      else if (width < 1024) itemsPerSlide = 2;
      else if (width < 1280) itemsPerSlide = 3;
      
      setCardsPerPage(itemsPerSlide);
      setTotalPages(Math.ceil(recentTools.length / itemsPerSlide));
    }

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [recentTools.length]);

  // Track active slide based on scroll offset
  const handleScroll = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, clientWidth } = scrollRef.current;
    if (clientWidth === 0) return;
    
    const pageIndex = Math.round(scrollLeft / clientWidth);
    setActiveIndex(pageIndex);
  };

  // Scroll to a specific page index
  const scrollToPage = (index: number) => {
    if (!scrollRef.current) return;
    const clientWidth = scrollRef.current.clientWidth;
    scrollRef.current.scrollTo({
      left: index * clientWidth,
      behavior: 'smooth',
    });
    setActiveIndex(index);
  };

  return (
    <div className="space-y-8 w-full">
      {/* Scrollable Container */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex gap-4 sm:gap-5 overflow-x-auto snap-x snap-mandatory scroll-smooth no-scrollbar pb-4"
        style={{ scrollbarWidth: 'none' }}
      >
        {recentTools.map((tool) => {
          if (!tool) return null;
          const words = tool.description_short.split(' ');
          const excerpt = words.length > 12 ? words.slice(0, 12).join(' ') + '...' : tool.description_short;
          const professionName = professionNameMap[tool.profession_slug] || tool.profession_slug;
          const toolCats = tool.categories || [];
          const detailUrl = `${localePrefix}/${locale === 'fr' ? toFrenchProfessionSlug(tool.profession_slug) : tool.profession_slug}/${getSubCategorySlug(tool.profession_slug, toolCats[0] || '') || 'tools'}/${tool.slug}`;

          return (
            <div
              key={tool.id}
              className="snap-start shrink-0 w-full sm:w-[calc(50%-10px)] lg:w-[calc(33.333%-14px)] xl:w-[calc(25%-15px)] flex flex-col"
            >
              <Card className="h-full overflow-hidden glass-card transition-all duration-500 hover:bg-card/60 hover:border-primary/30 hover:shadow-[0_12px_40px_rgba(168,85,247,0.12)] hover:scale-[1.01] group/card relative rounded-[1.5rem] sm:rounded-[2rem] flex flex-col justify-between">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover/card:opacity-100 pointer-events-none" />
                <CardHeader className="relative z-10 p-5 flex flex-col h-full flex-grow">
                  
                  {/* Logo and Badges */}
                  <div className="flex w-full items-start justify-between mb-4">
                    <Link href={detailUrl} className="h-10 w-10 sm:h-12 sm:w-12 rounded-2xl bg-primary/5 overflow-hidden flex items-center justify-center border border-primary/10 shadow-inner shrink-0 transition-transform duration-500 group-hover/card:scale-110 group-hover/card:rotate-3">
                      <SmartLogo
                        logoUrl={tool.logo_url}
                        websiteUrl={tool.website_url}
                        name={tool.name}
                        size={48}
                        className="h-full w-full object-contain p-2"
                      />
                    </Link>
                    <Badge className="bg-primary/5 text-primary/80 border-primary/10 text-[0.6rem] font-bold uppercase px-2 py-0.5 rounded-full shadow-sm shrink-0 mt-1">
                      Featured
                    </Badge>
                  </div>
                  
                  {/* Clickable Title and Description */}
                  <Link href={detailUrl} className="block group-hover/card:text-foreground">
                    <CardTitle className="text-[1.1rem] sm:text-xl font-bold tracking-tight text-foreground/90 mb-2 group-hover/card:text-primary transition-colors line-clamp-1 w-full">
                      {tool.name}
                    </CardTitle>
                    <CardDescription className="text-[0.8rem] sm:text-[0.85rem] leading-relaxed text-muted-foreground/80 group-hover/card:text-muted-foreground transition-colors line-clamp-3 w-full">
                      {excerpt}
                    </CardDescription>
                  </Link>

                  {/* Card Footer: Category, Views & Compare action */}
                  <div className="mt-auto pt-4 border-t border-border/50 flex w-full items-center justify-between gap-2">
                    <Badge className="bg-card text-muted-foreground border-border/60 text-[0.6rem] sm:text-[0.65rem] font-medium px-2 py-0.5 rounded-md truncate max-w-[100px] sm:max-w-full">
                      {professionName}
                    </Badge>

                    <div className="flex items-center gap-2.5">
                      {tool.view_count != null && tool.view_count > 0 && (
                        <div className="text-[0.65rem] font-medium text-muted-foreground/60 flex items-center shrink-0">
                          {tool.view_count >= 1000
                            ? `${(tool.view_count / 1000).toFixed(1)}k views`
                            : `${tool.view_count} views`}
                        </div>
                      )}
                      
                      <Link
                        href={`${localePrefix}/compare?tool=${tool.slug}`}
                        className="text-[10px] font-bold text-primary hover:underline uppercase tracking-wider shrink-0 flex items-center gap-1 border-l border-border/50 pl-2.5"
                      >
                        Compare
                        <ArrowLeftRight className="h-3 w-3" aria-hidden="true" />
                      </Link>
                    </div>
                  </div>

                </CardHeader>
              </Card>
            </div>
          );
        })}
      </div>

      {/* Bubble Pagination Indicators */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-4" aria-label="Slider pagination">
          {Array.from({ length: totalPages }).map((_, idx) => (
            <button
              key={idx}
              onClick={() => scrollToPage(idx)}
              className="h-10 w-10 flex items-center justify-center group -mx-1"
              aria-label={`Go to slide page ${idx + 1}`}
              aria-current={activeIndex === idx ? 'true' : undefined}
            >
              <div
                className={`h-2.5 rounded-full transition-all duration-300 ${
                  activeIndex === idx
                    ? 'bg-primary w-6'
                    : 'bg-muted-foreground/30 group-hover:bg-muted-foreground/50 w-2.5'
                }`}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
